<template>
  <div class="vc-speech-params" :class="{ 'is-expanded': expanded }">
    <div class="vc-speech-params__head" @click="expanded = !expanded">
      <span class="vc-speech-params__title">高级参数</span>
      <span class="vc-speech-params__arrow" aria-hidden="true">
        <Icon :type="expanded ? 'ios-arrow-up' : 'ios-arrow-down'" size="16" />
      </span>
    </div>
    <div v-show="expanded" class="vc-speech-params__body">
      <div class="vc-speech-params__toolbar">
        <span class="vc-param-meta">{{ paramCount }}/{{ maxParams }}</span>
        <AddParamLinkButton
          :disabled="paramCount >= maxParams"
          @click="addParamRow"
        />
      </div>
      <div class="vc-param-table-head">
        <span class="vc-param-drag-col" />
        <span class="vc-param-key-col">参数名</span>
        <span class="vc-param-name-col">参数值</span>
        <span class="vc-param-del-col" />
      </div>
      <draggable v-model="speechParamsSync" handle=".vc-param-drag" class="vc-param-list" :animation="180">
        <div v-for="(row, index) in speechParamsSync" :key="'sp-' + index" class="vc-param-row">
          <span class="vc-param-drag"><Icon type="md-menu" size="20" /></span>
          <Input v-model="row.paramKey" class="vc-param-input-key" placeholder="参数名" />
          <Input v-model="row.paramName" class="vc-param-input-name" placeholder="请输入参数值" />
          <Poptip
            :value="exclusiveConfirmKey === paramConfirmKey(index)"
            confirm
            placement="top-end"
            transfer
            :title="paramDeleteConfirmTitle(row)"
            ok-text="确定"
            cancel-text="取消"
            @click.native.stop
            @input="onExclusiveConfirmInput(paramConfirmKey(index), $event)"
            @on-ok="removeParamRow(index)"
          >
            <span class="vc-param-del-trigger">
              <DeleteIconButton size="sm" title="删除" />
            </span>
          </Poptip>
        </div>
      </draggable>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import DeleteIconButton from '../shared/DeleteIconButton.vue'
import AddParamLinkButton from '../shared/AddParamLinkButton.vue'
import { speechParamRowsHaveValues } from '../../utils/flowSectionHelpers'
import { exclusiveConfirmMixin } from '../../utils/exclusiveConfirm'

export default {
  name: 'SpeechParamsSection',
  mixins: [exclusiveConfirmMixin],
  components: { draggable, DeleteIconButton, AddParamLinkButton },
  props: {
    speechParams: {
      type: Array,
      required: true,
    },
    maxParams: {
      type: Number,
      default: 15,
    },
    defaultExpanded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expanded: this.defaultExpanded,
    }
  },
  computed: {
    speechParamsSync: {
      get() {
        return this.speechParams
      },
      set(v) {
        this.$emit('update:speechParams', v)
      },
    },
    paramCount() {
      return (this.speechParams && this.speechParams.length) || 0
    },
    hasFilledParams() {
      return speechParamRowsHaveValues(this.speechParams)
    },
  },
  watch: {
    hasFilledParams: {
      immediate: true,
      handler(hasValue) {
        if (hasValue) this.expanded = true
      },
    },
  },
  methods: {
    paramConfirmKey(index) {
      return `speech-param-${this._uid}-${index}`
    },
    paramDeleteConfirmTitle(row) {
      if (!row) return '确定要删除该参数吗？'
      const n = row.paramName != null ? String(row.paramName).trim() : ''
      const k = row.paramKey != null ? String(row.paramKey).trim() : ''
      if (n) return `确定删除参数「${n}」吗？`
      if (k) return `确定删除参数「${k}」吗？`
      return '确定要删除该参数吗？'
    },
    addParamRow() {
      if (this.paramCount >= this.maxParams) return
      this.speechParams.push({ paramName: '', paramKey: '' })
    },
    removeParamRow(index) {
      this.closeExclusiveConfirm(this.paramConfirmKey(index))
      this.speechParams.splice(index, 1)
    },
  },
}
</script>

<style lang="scss" scoped>
@import './shared.scss';

.vc-speech-params {
  margin-top: 20px;
  border: 1px solid #e8eaec;
  border-radius: 8px;
  overflow: hidden;
  background: #fafbfc;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    cursor: pointer;
    user-select: none;
    background: #f4f7fa;
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
    color: #262626;
  }

  &__arrow {
    color: #909399;
    display: inline-flex;
  }

  &__body {
    padding: 12px 14px 16px;
    background: #fff;
    border-top: 1px solid #e8eaec;
  }

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    margin-bottom: 8px;
  }
}

.vc-param-meta {
  font-size: 13px;
  color: #8c8c8c;
}

.vc-param-table-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0 10px;
  font-size: 12px;
  font-weight: 600;
  color: #595959;
  border-bottom: 1px solid #f0f0f0;

  .vc-param-name-col,
  .vc-param-key-col {
    text-align: left;
  }
}

.vc-param-drag-col {
  width: 28px;
}
.vc-param-name-col {
  flex: 1;
  min-width: 0;
}
.vc-param-key-col {
  flex: 1;
  min-width: 0;
}
.vc-param-del-col {
  width: 36px;
}

.vc-param-list {
  padding-top: 8px;
}

.vc-param-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.vc-param-drag {
  width: 28px;
  text-align: center;
  cursor: grab;
  color: #bfbfbf;

  &:active {
    cursor: grabbing;
  }
}

.vc-param-input-name,
.vc-param-input-key {
  flex: 1;
  min-width: 0;
}

.vc-param-del-trigger {
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  outline: none;

  &:focus-visible {
    outline: none;
  }
}
</style>
